import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { formatDate, formatDateTime } from '@/lib/format'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { StatusBadge } from '@/components/StatusBadge'
import { Pagination } from '@/components/Pagination'
import { EmptyState } from '@/components/EmptyState'
import { ListChecks, StopCircle, Loader2 } from 'lucide-react'
import { useEnrollments, useStopEnrollment } from '@/hooks/useApi'

export function ContactEnrollmentList({ contactId }: { contactId: string }) {
  const { t } = useTranslation('contacts')
  const [page, setPage] = useState(1)
  const { data, isLoading } = useEnrollments(page, 50, {
    contact_id: contactId,
  })
  const stopEnrollment = useStopEnrollment()

  const handleStop = (id: string) => {
    if (confirm(t('enrollments.confirmStop'))) {
      stopEnrollment.mutate(id)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }

  if (!data || data.items.length === 0) {
    return (
      <EmptyState
        icon={ListChecks}
        title={t('enrollments.empty.title')}
        description={t('enrollments.empty.description')}
      />
    )
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('enrollments.table.sequence')}</TableHead>
            <TableHead>{t('enrollments.table.status')}</TableHead>
            <TableHead className="text-center">{t('enrollments.table.step')}</TableHead>
            <TableHead>{t('enrollments.table.nextStep')}</TableHead>
            <TableHead>{t('enrollments.table.start')}</TableHead>
            <TableHead>{t('enrollments.table.completed')}</TableHead>
            <TableHead className="text-right">{t('enrollments.table.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.items.map((enrollment) => (
            <TableRow key={enrollment.id}>
              <TableCell>
                {enrollment.sequence ? (
                  <Link
                    to={`/sequences/${enrollment.sequence.id}`}
                    className="hover:underline font-medium"
                  >
                    {enrollment.sequence.name}
                  </Link>
                ) : (
                  <span className="text-muted-foreground">{t('enrollments.unknownSequence')}</span>
                )}
              </TableCell>
              <TableCell>
                <StatusBadge status={enrollment.status} />
              </TableCell>
              <TableCell className="text-center">
                {enrollment.current_step}
              </TableCell>
              <TableCell>
                {enrollment.next_step_at
                  ? formatDateTime(enrollment.next_step_at)
                  : '-'}
              </TableCell>
              <TableCell>
                {formatDate(enrollment.started_at)}
              </TableCell>
              <TableCell>
                {enrollment.completed_at
                  ? formatDate(enrollment.completed_at)
                  : '-'}
              </TableCell>
              <TableCell className="text-right">
                {enrollment.status === 'active' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleStop(enrollment.id)}
                    disabled={stopEnrollment.isPending}
                  >
                    {stopEnrollment.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <StopCircle className="h-4 w-4 text-red-500" />
                    )}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {data.pages > 1 && (
        <div className="mt-4">
          <Pagination
            page={page}
            totalPages={data.pages}
            onPageChange={setPage}
          />
        </div>
      )}
    </>
  )
}
